import type { ColumnDef } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { useMemo } from 'react';
import { formatToCompactCurrency, formatToCompactNumber } from 'src/common/utils/format';

import { DataTable } from '@/components/composed';
import { Badge, Text } from '@/design-system/components/ui';

import { filterTokenEarnings, type GlobalBreakdownResult } from '../domain/types';
import { MultiCurrencyEarnings } from './MultiCurrencyEarnings';

interface BreakdownTableProps {
  results: GlobalBreakdownResult[];
  isLoading: boolean;
}

export function BreakdownTable({ results, isLoading }: BreakdownTableProps) {
  const columns = useMemo<ColumnDef<GlobalBreakdownResult>[]>(
    () => [
      {
        accessorKey: 'groupKey',
        header: 'Group',
        // Mirror BreakdownChart: a null/empty group key (no region/status/tier)
        // renders as "Unknown" so the chart and table stay consistent.
        cell: ({ getValue }) => (
          <Badge variant="default" size="sm">
            {getValue<string>() || 'Unknown'}
          </Badge>
        ),
      },
      {
        accessorKey: 'affiliates',
        header: 'Affiliates',
        cell: ({ getValue }) => getValue<number>().toLocaleString(),
      },
      {
        accessorKey: 'r1Volume',
        header: 'Level 1 Volume',
        cell: ({ getValue }) => formatToCompactCurrency(getValue<number>()),
      },
      {
        accessorKey: 'indirectVolume',
        header: 'Indirect Volume',
        cell: ({ getValue }) => formatToCompactCurrency(getValue<number>()),
      },
      {
        accessorKey: 'totalReferralVolume',
        header: 'Total Volume',
        cell: ({ getValue }) => formatToCompactCurrency(getValue<number>()),
      },
      {
        accessorKey: 'revenueFromReferrals',
        header: 'Revenue',
        cell: ({ getValue }) => formatToCompactCurrency(getValue<number>()),
      },
      {
        accessorKey: 'earnings',
        header: 'Earnings',
        cell: ({ row }) => (
          <MultiCurrencyEarnings earnings={filterTokenEarnings(row.original.earnings)} compact scaleByDecimals />
        ),
      },
      {
        accessorKey: 'pointsPaid',
        header: 'Points Paid',
        cell: ({ getValue }) => formatToCompactNumber(getValue<number>()),
      },
    ],
    [],
  );

  return (
    <DataTable<GlobalBreakdownResult>
      columns={columns}
      data={results}
      loading={isLoading}
      scrollable
      tableClassName="tw:whitespace-nowrap"
      getRowId={(row) => row.groupKey || 'Unknown'}
      emptyState={
        <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-16 tw:gap-4">
          <div className="tw:flex tw:items-center tw:justify-center tw:text-muted-foreground">
            <Search size={32} strokeWidth={1.5} />
          </div>
          <Text variant="body-md" color="muted" className="tw:text-center">
            No data available
          </Text>
        </div>
      }
    />
  );
}
